import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Moneda } from '../types';
import { changeLanguage } from '../i18n';

// Exchange rates (approximate, update via API in production)
const EXCHANGE_RATES: Record<string, number> = {
    'ARS_USD': 0.001,
    'USD_ARS': 1000,
    'USD_EUR': 0.92,
    'EUR_USD': 1.09,
    'USD_BRL': 5.0,
    'USD_CLP': 950,
    'USD_PYG': 7300,
    'USD_MXN': 17,
    'USD_COP': 3900,
    'USD_PEN': 3.7,
    'USD_UYU': 39,
};

// Country to currency mapping
const COUNTRY_CURRENCY: Record<string, Moneda> = {
    'AR': Moneda.ARS,
    'US': Moneda.USD,
    'ES': Moneda.EUR,
    'BR': Moneda.BRL,
    'CL': Moneda.CLP,
    'PY': Moneda.PYG,
    'UY': Moneda.UYU,
    'MX': Moneda.MXN,
    'CO': Moneda.COP,
    'PE': Moneda.PEN,
    'BO': Moneda.BOB,
    'VE': Moneda.VES,
};

interface CurrencyContextType {
    displayCurrency: Moneda;
    setDisplayCurrency: (currency: Moneda) => void;
    userCountry: string | null;
    convertPrice: (price: number, fromCurrency: Moneda) => number;
    formatPrice: (price: number, currency?: Moneda) => string;
    getCurrencySymbol: (currency: Moneda) => string;
    isNonArgentine: boolean;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const CurrencyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [displayCurrency, setDisplayCurrency] = useState<Moneda>(Moneda.ARS);
    const [userCountry, setUserCountry] = useState<string | null>(() => {
        return localStorage.getItem('selectedCountry');
    });

    const detectCountry = async () => {
        // If already have a selection in storage, respect it (prevents flips on load)
        if (localStorage.getItem('selectedCountry')) {
            const saved = localStorage.getItem('selectedCountry');
            setUserCountry(saved);

            // Set currency based on saved country
            if (saved) {
                const defaultCurrency = COUNTRY_CURRENCY[saved] || Moneda.USD;
                if (saved !== 'AR') setDisplayCurrency(defaultCurrency);
            }
            return;
        }

        try {
            // Use a free geolocation API
            const response = await fetch('https://ipapi.co/json/');
            const data = await response.json();
            const country = data.country_code || 'AR';
            setUserCountry(country);

            // Per user request, we sync this to localStorage as the initial "selected" country
            localStorage.setItem('selectedCountry', country);

            // Set default currency based on country
            const defaultCurrency = COUNTRY_CURRENCY[country] || Moneda.USD;
            if (country !== 'AR') {
                setDisplayCurrency(defaultCurrency);
            }

            // Sync language if no manual preference exists
            const savedLng = localStorage.getItem('i18nextLng');
            if (!savedLng) {
                if (country === 'BR') changeLanguage('pt');
                else if (country === 'US' || country === 'CA') changeLanguage('en');
                else changeLanguage('es');
            }
        } catch (error) {

            setUserCountry('AR');
        }
    };

    // Detect user country/currency on mount
    useEffect(() => {
        detectCountry();
    }, []);

    // Listen for country changes
    useEffect(() => {
        const handleCountryChange = (event: Event) => {
            const customEvent = event as CustomEvent;
            const country = customEvent.detail.country;
            setUserCountry(country);

            // Set currency based on country logic
            const targetCurrency = COUNTRY_CURRENCY[country] || Moneda.USD;
            setDisplayCurrency(targetCurrency);
        };

        window.addEventListener('countryChanged', handleCountryChange);
        return () => window.removeEventListener('countryChanged', handleCountryChange);
    }, []);

    // Save preference
    useEffect(() => {
        localStorage.setItem('display_currency', displayCurrency);
    }, [displayCurrency]);

    const isNonArgentine = userCountry !== null && userCountry !== 'AR';

    const convertPrice = useCallback((price: number, fromCurrency: Moneda): number => {
        if (fromCurrency === displayCurrency) return price;

        // Base case: If converting USD to Local
        if (fromCurrency === Moneda.USD) {
            const key = `USD_${displayCurrency}`;
            const rate = EXCHANGE_RATES[key] || 1;
            return Math.round(price * rate * 100) / 100;
        }

        // Converting Local to USD
        if (displayCurrency === Moneda.USD) {
            // TODO: Add reverse rates or invert
            return price; // Placeholder if we don't have rate
        }

        // General case via USD (Plan price is usually USD or ARS)
        const key = `${fromCurrency}_${displayCurrency}`;
        const rate = EXCHANGE_RATES[key];

        if (rate) {
            return Math.round(price * rate * 100) / 100;
        }

        // If direct conversion not available, convert via USD
        // From -> USD -> To
        const toUSD = EXCHANGE_RATES[`${fromCurrency}_USD`] || 0;
        const fromUSD = EXCHANGE_RATES[`USD_${displayCurrency}`] || 0;

        if (toUSD && fromUSD) {
            return Math.round(price * toUSD * fromUSD * 100) / 100;
        }

        return price;
    }, [displayCurrency]);

    const getCurrencySymbolLocal = useCallback((currency: Moneda): string => {
        const symbols: Record<Moneda, string> = {
            [Moneda.ARS]: '$',
            [Moneda.USD]: 'u$d',
            [Moneda.EUR]: '€',
            [Moneda.BRL]: 'R$',
            [Moneda.CLP]: 'CLP',
            [Moneda.UYU]: '$U',
            [Moneda.MXN]: 'MX$',
            [Moneda.COP]: 'COL$',
            [Moneda.PEN]: 'S/',
            [Moneda.PYG]: 'Gs',
            [Moneda.BOB]: 'Bs',
            [Moneda.VES]: 'Bs.S',
            [Moneda.GBP]: '£',
            [Moneda.CHF]: 'CHF',
            [Moneda.CAD]: 'C$',
            [Moneda.JPY]: '¥',
            [Moneda.CNY]: '¥',
            [Moneda.INR]: '₹',
            [Moneda.KRW]: '₩',
            [Moneda.AUD]: 'A$',
            [Moneda.NZD]: 'NZ$',
        };
        return symbols[currency] || '$';
    }, []);

    const formatPrice = useCallback((price: number, currency?: Moneda): string => {
        const curr = currency || displayCurrency;
        const symbol = getCurrencySymbolLocal(curr);
        return `${symbol}${price.toLocaleString('es-AR', { minimumFractionDigits: curr === Moneda.ARS ? 0 : 2, maximumFractionDigits: 2 })}`;
    }, [displayCurrency, getCurrencySymbolLocal]);

    return (
        <CurrencyContext.Provider value={{
            displayCurrency,
            setDisplayCurrency,
            userCountry,
            convertPrice,
            formatPrice,
            getCurrencySymbol: getCurrencySymbolLocal,
            isNonArgentine
        }}>
            {children}
        </CurrencyContext.Provider>
    );
};

export const useCurrency = (): CurrencyContextType => {
    const context = useContext(CurrencyContext);
    if (!context) {
        throw new Error('useCurrency must be used within CurrencyProvider');
    }
    return context;
};

export default CurrencyContext;
